import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Icon from '../../components/AppIcon';

import VideoModal from './components/VideoModal';
import AddVideoModal from './components/AddVideoModal';
import SongLevelGrid, { LEVELS } from './components/SongLevelGrid';

const LEVEL_LABELS = {
  overview: 'Overview',
  level_1: 'Level 1',
  level_2: 'Level 2',
  level_3: 'Level 3',
  level_4: 'Level 4',
  level_5: 'Level 5',
};

const AllVideosManagement = () => {
  const { isSuperUser } = useAuth();

  // Data state
  const [songs, setSongs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [songTalentMap, setSongTalentMap] = useState({}); // { songId: talentId }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [missingLevelFilter, setMissingLevelFilter] = useState('');
  const [hasVideosFilter, setHasVideosFilter] = useState(false); // filter: only songs with at least one video
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddVideoModal, setShowAddVideoModal] = useState(false);
  const [toggling, setToggling] = useState(null); // videoId being toggled
  const [bulkLevelTarget, setBulkLevelTarget] = useState('overview');
  const [bulkAction, setBulkAction] = useState('publish');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    if (!isSuperUser()) {
      setError('Access denied. SuperUser privileges required.');
      setLoading(false);
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch songs with musical info
      const { data: songsData, error: songsError } = await supabase
        ?.from('songs')
        ?.select('id, title, musical_id, musicals(id, title)')
        ?.order('title');

      if (songsError) throw songsError;

      // Fetch all videos with level info from the mux videos table
      const { data: videosData, error: videosError } = await supabase
        ?.from('videos')
        ?.select('id, title, status, mux_playback_id, duration_seconds, published, level, song_id, talent_id, created_at, updated_at')
        ?.order('created_at', { ascending: false });

      if (videosError) throw videosError;

      // Fetch roles with assigned_talent_id and their song associations via character_song_analyses
      // This lets us know which talent is assigned to a role that covers a song
      const { data: rolesData, error: rolesError } = await supabase
        ?.from('character_song_analyses')
        ?.select(`
          song_id,
          roles:role_id (
            id,
            assigned_talent_id,
            talent_profiles:assigned_talent_id (
              id,
              first_name,
              last_name,
              display_name
            )
          )
        `)
        ?.not('role_id', 'is', null);

      if (rolesError) {
        console.warn('Could not fetch role-talent associations:', rolesError);
      }

      // Build songTalentMap: { songId: talentId } — use first assigned talent found per song
      const talentMap = {};
      const talentNameMap = {};
      rolesData?.forEach(row => {
        const songId = row?.song_id;
        const talentId = row?.roles?.assigned_talent_id;
        if (songId && talentId && !talentMap?.[songId]) {
          talentMap[songId] = talentId;
          const tp = row?.roles?.talent_profiles;
          if (tp) {
            talentNameMap[songId] = tp?.display_name || `${tp?.first_name || ''} ${tp?.last_name || ''}`?.trim();
          }
        }
      });

      // Attach talentName to songs for display
      const enrichedSongs = (songsData || [])?.map(s => ({
        ...s,
        talentName: talentNameMap?.[s?.id] || null,
      }));

      setSongs(enrichedSongs);
      setVideos(videosData || []);
      setSongTalentMap(talentMap);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Build videosBySong: { songId: { level_key: video } }
  const videosBySong = useMemo(() => {
    const map = {};
    videos?.forEach(v => {
      if (!v?.song_id || !v?.level) return;
      if (!map?.[v?.song_id]) map[v.song_id] = {};
      // If multiple videos for same song+level, keep the most recent ready one, else latest
      const existing = map?.[v?.song_id]?.[v?.level];
      if (!existing) {
        map[v.song_id][v.level] = v;
      } else {
        if (v?.status === 'ready' && existing?.status !== 'ready') {
          map[v.song_id][v.level] = v;
        } else if (v?.status !== 'ready' && existing?.status === 'ready') {
          // keep existing
        } else if (new Date(v.created_at) > new Date(existing.created_at)) {
          map[v.song_id][v.level] = v;
        }
      }
    });
    return map;
  }, [videos]);

  // Filter songs by search and hasVideos toggle
  const filteredSongs = useMemo(() => {
    let result = songs;

    if (searchQuery?.trim()) {
      const q = searchQuery?.toLowerCase();
      result = result?.filter(s =>
        s?.title?.toLowerCase()?.includes(q) ||
        s?.musicals?.title?.toLowerCase()?.includes(q)
      );
    }

    if (hasVideosFilter) {
      result = result?.filter(s => {
        const sv = videosBySong?.[s?.id];
        return sv && Object.keys(sv)?.length > 0;
      });
    }

    return result;
  }, [songs, searchQuery, hasVideosFilter, videosBySong]);

  // Summary stats
  const stats = useMemo(() => {
    let totalVideos = 0;
    let readyVideos = 0;
    let fullySongs = 0;
    songs?.forEach(song => {
      const sv = videosBySong?.[song?.id] || {};
      let songReady = 0;
      LEVELS?.forEach(l => {
        const v = sv?.[l?.key];
        if (v) {
          totalVideos++;
          if (v?.status === 'ready') { readyVideos++; songReady++; }
        }
      });
      if (songReady === 6) fullySongs++;
    });
    return { totalVideos, readyVideos, fullySongs, totalSongs: songs?.length };
  }, [songs, videosBySong]);

  // Toggle publish for a single video
  const handleTogglePublish = useCallback(async (video) => {
    setToggling(video?.id);
    try {
      const { error } = await supabase
        ?.from('videos')
        ?.update({ published: !video?.published, updated_at: new Date()?.toISOString() })
        ?.eq('id', video?.id);
      if (error) throw error;
      setVideos(prev => prev?.map(v => v?.id === video?.id ? { ...v, published: !v?.published } : v));
    } catch (err) {
      console.error('Toggle publish error:', err);
    } finally {
      setToggling(null);
    }
  }, []);

  // Bulk publish/unpublish by level
  const handleBulkLevelAction = async () => {
    if (!bulkLevelTarget) return;
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const targetVideos = videos?.filter(v => v?.level === bulkLevelTarget && v?.status === 'ready');
      const newPublished = bulkAction === 'publish';
      const ids = targetVideos?.map(v => v?.id);

      if (ids?.length === 0) {
        setBulkResult({ type: 'info', message: `No ready videos found for ${LEVEL_LABELS?.[bulkLevelTarget]}.` });
        return;
      }

      const { error } = await supabase
        ?.from('videos')
        ?.update({ published: newPublished, updated_at: new Date()?.toISOString() })
        ?.in('id', ids);

      if (error) throw error;

      setVideos(prev => prev?.map(v =>
        ids?.includes(v?.id) ? { ...v, published: newPublished } : v
      ));
      setBulkResult({
        type: 'success',
        message: `${bulkAction === 'publish' ? 'Published' : 'Unpublished'} ${ids?.length} ready video${ids?.length !== 1 ? 's' : ''} for ${LEVEL_LABELS?.[bulkLevelTarget]}.`
      });
    } catch (err) {
      console.error('Bulk action error:', err);
      setBulkResult({ type: 'error', message: 'Bulk action failed. Please try again.' });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleViewVideo = (video) => {
    setSelectedVideo(video);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-7xl">
          <div className="flex items-center justify-center h-96">
            <LoadingSpinner size="lg" />
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>All Videos Management - RoleCall</title>
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
          <Header />
          <main className="container mx-auto px-6 py-8 max-w-7xl">
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-12 text-center">
              <Icon name="AlertCircle" size={32} className="text-red-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-red-800 mb-3">Access Denied</h3>
              <p className="text-red-700 text-sm">{error}</p>
              <Button variant="outline" onClick={() => window.location?.reload()} className="border-red-400 text-red-600">
                Try Again
              </Button>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>All Videos Management - RoleCall</title>
        <meta name="description" content="Manage videos grouped by song and level with bulk organization tools." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-7xl">

          {/* Page header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white mb-1">All Videos Management</h1>
            <p className="text-blue-100">Videos organized by song and level — Overview through Level 5</p>
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Songs', value: stats?.totalSongs, icon: 'Music', color: 'text-blue-300' },
              { label: 'Total Videos', value: stats?.totalVideos, icon: 'Video', color: 'text-purple-300' },
              { label: 'Ready Videos', value: stats?.readyVideos, icon: 'CheckCircle', color: 'text-green-300' },
              { label: 'Fully Complete', value: stats?.fullySongs, icon: 'Star', color: 'text-yellow-300' },
            ]?.map(s => (
              <div key={s?.label} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-4 flex items-center gap-3">
                <Icon name={s?.icon} size={22} className={s?.color} />
                <div>
                  <div className="text-2xl font-bold text-white">{s?.value}</div>
                  <div className="text-xs text-blue-200">{s?.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Bulk Level Tools */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-5 mb-6">
            <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <Icon name="Layers" size={16} className="text-blue-300" />
              Bulk Level Organization
            </h2>
            <div className="flex flex-wrap items-end gap-4">
              {/* Bulk publish/unpublish */}
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs text-blue-200 mb-1">Action</label>
                  <select
                    value={bulkAction}
                    onChange={e => setBulkAction(e?.target?.value)}
                    className="px-3 py-2 rounded-lg bg-white bg-opacity-20 border border-white border-opacity-30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="publish" className="text-gray-800">Publish</option>
                    <option value="unpublish" className="text-gray-800">Unpublish</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-blue-200 mb-1">Level</label>
                  <select
                    value={bulkLevelTarget}
                    onChange={e => setBulkLevelTarget(e?.target?.value)}
                    className="px-3 py-2 rounded-lg bg-white bg-opacity-20 border border-white border-opacity-30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    {LEVELS?.map(l => (
                      <option key={l?.key} value={l?.key} className="text-gray-800">{l?.label}</option>
                    ))}
                  </select>
                </div>
                <Button
                  variant="default"
                  onClick={handleBulkLevelAction}
                  disabled={bulkLoading}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white border-0 rounded-lg text-sm"
                  iconName={bulkLoading ? 'Loader' : 'Zap'}
                  iconPosition="left"
                >
                  {bulkLoading ? 'Processing…' : `Apply to All Songs`}
                </Button>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-10 bg-white bg-opacity-20" />

              {/* Missing level filter */}
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs text-blue-200 mb-1">Show songs missing</label>
                  <select
                    value={missingLevelFilter}
                    onChange={e => setMissingLevelFilter(e?.target?.value)}
                    className="px-3 py-2 rounded-lg bg-white bg-opacity-20 border border-white border-opacity-30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="" className="text-gray-800">— No filter —</option>
                    {LEVELS?.map(l => (
                      <option key={l?.key} value={l?.key} className="text-gray-800">{l?.label}</option>
                    ))}
                  </select>
                </div>
                {missingLevelFilter && (
                  <button
                    onClick={() => setMissingLevelFilter('')}
                    className="text-xs text-red-300 hover:text-white flex items-center gap-1 transition-colors pb-2"
                  >
                    <Icon name="X" size={12} />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Bulk result feedback */}
            {bulkResult && (
              <div className={`mt-3 flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                bulkResult?.type === 'success' ? 'bg-green-500 bg-opacity-20 text-green-200' :
                bulkResult?.type === 'error'? 'bg-red-500 bg-opacity-20 text-red-200' : 'bg-blue-500 bg-opacity-20 text-blue-200'
              }`}>
                <Icon name={bulkResult?.type === 'success' ? 'CheckCircle' : bulkResult?.type === 'error' ? 'AlertCircle' : 'Info'} size={14} />
                {bulkResult?.message}
                <button onClick={() => setBulkResult(null)} className="ml-auto hover:opacity-70">
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
          </div>

          {/* Search + controls bar */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-48">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-200" />
                <input
                  type="text"
                  placeholder="Search songs or musicals…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e?.target?.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-lg bg-white bg-opacity-20 border border-white border-opacity-30 text-white placeholder-blue-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* Has Videos filter toggle */}
              <button
                onClick={() => setHasVideosFilter(prev => !prev)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                  hasVideosFilter
                    ? 'bg-indigo-500 bg-opacity-40 border-indigo-400 border-opacity-60 text-indigo-100' :'bg-white bg-opacity-10 border-white border-opacity-20 text-blue-200 hover:bg-opacity-20'
                }`}
                title="Show only songs that have at least one video"
              >
                <Icon name="Video" size={14} />
                Has Videos
              </button>

              {/* Active filter badges */}
              {missingLevelFilter && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-orange-500 bg-opacity-30 text-orange-200 border border-orange-400 border-opacity-40">
                  <Icon name="Filter" size={11} />
                  Missing: {LEVEL_LABELS?.[missingLevelFilter]}
                </span>
              )}

              <div className="flex items-center gap-2 ml-auto">
                <Button
                  variant="default"
                  onClick={() => setShowAddVideoModal(true)}
                  iconName="Plus"
                  iconPosition="left"
                  className="bg-green-600 hover:bg-green-700 text-white border-0 rounded-lg text-sm"
                >
                  Add Video
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchData}
                  iconName="RefreshCw"
                  iconPosition="left"
                  disabled={loading}
                  className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg text-sm"
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-blue-200">
            <span className="font-medium text-blue-100">Status:</span>
            {[
              { color: 'bg-green-400', label: 'Ready' },
              { color: 'bg-yellow-400', label: 'Preparing' },
              { color: 'bg-blue-400', label: 'Waiting' },
              { color: 'bg-red-400', label: 'Errored' },
              { color: 'bg-white bg-opacity-20', label: 'No video' },
            ]?.map(item => (
              <div key={item?.label} className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${item?.color}`} />
                {item?.label}
              </div>
            ))}
          </div>

          {/* Song level grid */}
          {filteredSongs?.length === 0 ? (
            <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 text-center">
              <Icon name="Music" size={40} className="text-blue-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">No songs found</h3>
              <p className="text-blue-200 text-sm">
                {hasVideosFilter ? 'No songs have videos yet. Upload a video to a song to see it here.' : 'Try adjusting your search query.'}
              </p>
            </div>
          ) : (
            <SongLevelGrid
              songs={filteredSongs}
              videosBySong={videosBySong}
              onTogglePublish={handleTogglePublish}
              onView={handleViewVideo}
              toggling={toggling}
              missingLevelFilter={missingLevelFilter}
              onVideoUploaded={fetchData}
              songTalentMap={songTalentMap}
            />
          )}

        </main>

        {/* Video preview modal */}
        {showModal && selectedVideo && (
          <VideoModal
            video={selectedVideo}
            onClose={() => { setShowModal(false); setSelectedVideo(null); }}
            onRefresh={fetchData}
          />
        )}

        <AddVideoModal
          isOpen={showAddVideoModal}
          onClose={() => setShowAddVideoModal(false)}
          onVideoAdded={fetchData}
        />
      </div>
    </>
  );
};

export default AllVideosManagement;